/* eslint-disable @typescript-eslint/no-use-before-define */
import debounce from 'debounce-promise';
import { useCallback, useMemo, useRef, useState } from 'react';

export interface IValidationResult {
  id?: string;
  valid: boolean;
  message: string;
}

type ValidationHandler<T> = (value: T) => Promise<IValidationResult[]>;

export type Validation<T> = {
  required: RequiredValidation<T>;
  match: MatchValidation<T>;
  custom: CustomValidation<T>;
  combine: CombineValidation<T>;
  results: (value: T) => Promise<IValidationResult[]>;
};

export type RequiredValidation<T> = (message: string, id?: string) => Validation<T>;
export type MatchValidation<T> = (regex: RegExp, message: string, id?: string) => Validation<T>;
export type CustomValidation<T> = (handler: (value: T) => boolean | Promise<boolean>, message: string, id?: string) => Validation<T>;
export type CombineValidation<T> = (validations: Validation<T>[]) => Validation<T>;

const createValidations = <T>(chain: ValidationHandler<T>[]): Validation<T> => ({
  required: createRequired<T>(chain),
  match: createMatch<T>(chain),
  custom: createCustom<T>(chain),
  combine: createCombine<T>(chain),
  results: async (value: T) => {
    return chain.reduce(async (previousResultsPromise, validate) => {
      // If previous in chain has invalid results don't continue;
      const previousResults = await previousResultsPromise;
      if (previousResults.some((x) => !x.valid)) {
        return previousResults;
      }

      const results = await validate(value);

      return [...previousResults, ...results];
    }, Promise.resolve<IValidationResult[]>([]));
  }
});

const createRequired =
  <T>(chain: ValidationHandler<T>[]) =>
  (message: string, id?: string) =>
    createValidations<T>([
      ...chain,
      async (value) => {
        if (typeof value === 'string') {
          return [{ valid: (value as string).trim() !== '', message, id }];
        }

        return [{ valid: value !== undefined && value !== null, message, id }];
      }
    ]);

const createMatch =
  <T>(chain: ValidationHandler<T>[]) =>
  (regex: RegExp, message: string, id?: string) =>
    createValidations<T>([
      ...chain,
      async (value) => {
        if (typeof value === 'string') {
          return [{ valid: regex.test(value as string), message, id }];
        }
        return [{ valid: false, message, id }];
      }
    ]);

const createCustom =
  <T>(chain: ValidationHandler<T>[]) =>
  (handler: (value: T) => boolean | Promise<boolean>, message: string, id?: string) =>
    createValidations<T>([...chain, async (value) => [{ valid: await handler(value), message, id }]]);

const createCombine =
  <T>(chain: ValidationHandler<T>[]) =>
  (validations: Validation<T>[]) =>
    createValidations<T>([
      ...chain,
      async (value) => {
        const results = (await Promise.all(validations.map(async (x) => x.results(value)))).flat();

        return results;
      }
    ]);

export namespace Validators {
  export const required = <T = any>(message: string, id?: string) => createRequired<T>([])(message, id);
  export const match = <T = any>(regex: RegExp, message: string, id?: string) => createMatch<T>([])(regex, message, id);
  export const custom = <T = any>(handler: (value: T) => boolean, message: string, id?: string) => createCustom<T>([])(handler, message, id);
  export const combine = <T = any>(validations: Validation<T>[]) => createCombine<T>([])(validations);
}

export interface IUseValidatorConfig {
  debounceMs?: number;
  validated?: boolean;
}

export interface IValidator<T> {
  errorMessage: string;
  handleEvent: (value: T, skipValidation?: boolean) => Promise<void>;
  /** Invalid is the result of the input being validated and the validation being unsuccessful (validated && !valid) */
  invalid: boolean;
  reset: () => void;
  results: IValidationResult[];
  /** Valid is the result of the input being validated and the validation being successful (validated && valid) */
  valid: boolean;
  validated: boolean;
  validating: boolean;
  validate: () => Promise<boolean>;
  value: T;
}

const DEFAULT_DEBOUNCE_MS = 400;
const DEFAULT_VALIDATING = false;
const DEFAULT_VALIDATED = false;
const DEFAULT_RESULTS: IValidationResult[] = [];

export const useValidator = <T>(
  initialValue: T,
  validation: Validation<T>,
  { debounceMs = DEFAULT_DEBOUNCE_MS, validated: initialValidated = DEFAULT_VALIDATED }: IUseValidatorConfig = {}
): IValidator<T> => {
  const [value, setValue] = useState<T>(initialValue);
  const [validating, setValidating] = useState(DEFAULT_VALIDATING);
  const [results, setResults] = useState<IValidationResult[]>(DEFAULT_RESULTS);

  const validatedRef = useRef(initialValidated);
  const lastValidatedValueRef = useRef<T>();

  const handleValidation = useCallback(
    async (newValue: T) => {
      setValidating(true);

      try {
        const newResults = await validation.results(newValue);
        validatedRef.current = true;
        setResults(newResults);

        lastValidatedValueRef.current = newValue;

        return newResults.every((x) => x.valid);
      } finally {
        setValidating(false);
      }
    },
    [validation]
  );

  const debouncedHandleValidation = useMemo(() => debounce(handleValidation, debounceMs), [debounceMs]);

  const handleEvent = async (newValue: T, skipValidation?: boolean) => {
    if (lastValidatedValueRef.current !== newValue || !validatedRef.current) {
      setValue(newValue);

      if (skipValidation) {
        return;
      }

      await (debounceMs > 0 ? debouncedHandleValidation(newValue) : handleValidation(newValue));
    }
  };

  const invalidResult = results.find((x) => !x.valid);
  const valid = validatedRef.current && !invalidResult;
  const invalid = validatedRef.current && !!invalidResult;

  const onReset = () => {
    validatedRef.current = initialValidated;
    setValue(initialValue);
    setValidating(DEFAULT_VALIDATING);
    setResults(DEFAULT_RESULTS);
    lastValidatedValueRef.current = undefined;
  };

  return {
    errorMessage: invalidResult?.message ?? '',
    handleEvent,
    invalid,
    reset: onReset,
    results,
    valid,
    validate: async () => handleValidation(value),
    validated: validatedRef.current,
    validating,
    value
  };
};
